import React, { useState, useRef, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import axios from 'axios';
import { McTypeahead } from "@maersk-global/mds-react-wrapper/components-core/mc-typeahead";
import { shareList } from './apihelpers';
import '../SubscriptionManagement.css';

/**
 * ShareModal Component
 * Modal for sharing subscription lists with other users
 * Uses MDS design patterns and user suggestion API
 */
const ShareModal = ({
    open,
    onClose,
    listId,
    listName,
    userEmail,
    userName,
    onShareSuccess,
}) => {
    const [selectedEmails, setSelectedEmails] = useState([]);
    const [defaultPermission, setDefaultPermission] = useState('read');
    const [suggestions, setSuggestions] = useState([]);
    const [editingPermissionFor, setEditingPermissionFor] = useState(null);
    const [isSharing, setIsSharing] = useState(false);
    const [message, setMessage] = useState('');
    
    const typeaheadRef = useRef(null);
    const dropdownRef = useRef(null);

    // Fetch user suggestions from API
    const fetchUserSuggestions = async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 3) {
            setSuggestions([]);
            if (typeaheadRef.current) {
                typeaheadRef.current.data = [];
            }
            return;
        }

        if (typeaheadRef.current) {
            typeaheadRef.current.loading = true;
        }

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URI}/api/landing_page/user_name_suggestions`,
                { user_name: searchTerm },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + localStorage.getItem('jwt_token'),
                    },
                }
            );

            if (response.status === 200 && response.data.status === 200) {
                const filteredSuggestions = (response.data.data || []).filter(
                    (user) => 
                        !selectedEmails.some((e) => e.email === user.sublabel) &&
                        user.sublabel?.toLowerCase() !== userEmail?.toLowerCase()
                );
                setSuggestions(filteredSuggestions);
                if (typeaheadRef.current) {
                    typeaheadRef.current.data = [...filteredSuggestions];
                }
            } else {
                setSuggestions([]);
                if (typeaheadRef.current) {
                    typeaheadRef.current.data = [];
                }
            }
        } catch (error) {
            console.error('Error fetching user suggestions:', error);
            setSuggestions([]);
            if (typeaheadRef.current) {
                typeaheadRef.current.data = [];
            }
        } finally {
            if (typeaheadRef.current) {
                typeaheadRef.current.loading = false;
            }
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setEditingPermissionFor(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleAddEmail = (email, name = '') => {
        if (email && !selectedEmails.some((e) => e.email === email)) {
            setSelectedEmails([
                ...selectedEmails,
                { email, name, permission: defaultPermission },
            ]);
            setSuggestions([]);
            if (typeaheadRef.current) {
                typeaheadRef.current.data = [];
                typeaheadRef.current.value = '';
                const inputElement = typeaheadRef.current.querySelector('input');
                if (inputElement) {
                    inputElement.value = '';
                }
            }
        }
    };

    const handleRemoveEmail = (emailToRemove) => {
        setSelectedEmails(selectedEmails.filter((e) => e.email !== emailToRemove));
    };

    const handleUpdatePermission = (email, permission) => {
        setSelectedEmails(
            selectedEmails.map((e) =>
                e.email === email ? { ...e, permission } : e
            )
        );
        setEditingPermissionFor(null);
    };

    const handleShare = async () => {
        if (selectedEmails.length === 0) {
            toast.error('Please add at least one email address', {
                position: 'top-center',
                autoClose: 3000,
            });
            return;
        }

        setIsSharing(true);
        try {
            // Separate emails by permission type
            const readEmails = selectedEmails
                .filter((e) => e.permission === 'read')
                .map((e) => e.email);
            const writeEmails = selectedEmails
                .filter((e) => e.permission === 'write')
                .map((e) => e.email);

            // Share with all users in a single API call
            const response = await shareList({
                userName:userName,
                userEmail: userEmail,
                listId,
                shareWithRead: readEmails,
                shareWithWrite: writeEmails,
            });

            const readCount = response.data?.read_access_granted || readEmails.length;
            const writeCount = response.data?.write_access_granted || writeEmails.length;

            let permissionText = '';
            if (readCount > 0 && writeCount > 0) {
                permissionText = ` (${readCount} read, ${writeCount} edit)`;
            } else if (readCount > 0) {
                permissionText = ' (read-only)';
            } else if (writeCount > 0) {
                permissionText = ' (edit)';
            }

            toast.success(
                `Subscription list "${listName}" shared with ${readCount + writeCount} ${
                    readCount + writeCount === 1 ? 'person' : 'people'
                }${permissionText}!`,
                {
                    position: 'top-center',
                    autoClose: 3000,
                }
            );

            if (onShareSuccess) {
                onShareSuccess();
            }
            handleClose();
        } catch (error) {
            toast.error(error.message || 'Failed to share list', {
                position: 'top-center',
                autoClose: 3000,
            });
        } finally {
            setIsSharing(false);
        }
    };

    const handleClose = () => {
        setSelectedEmails([]);
        setDefaultPermission('read');
        setEditingPermissionFor(null);
        setMessage('');
        setSuggestions([]);
        if (typeaheadRef.current) {
            typeaheadRef.current.data = [];
            typeaheadRef.current.value = '';
        }
        onClose();
    };

    if (!open) return null;

    const readCount = selectedEmails.filter((e) => e.permission === 'read').length;
    const editCount = selectedEmails.filter((e) => e.permission === 'write').length;

    return (
        <>
            {/* Backdrop */}
            <div
                className="share-modal-backdrop"
                onClick={handleClose}
                style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 9998,
                }}
            />

            {/* Modal */}
            <div
                className="share-modal-container"
                style={{
                    position: 'fixed',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                    zIndex: 9999,
                    width: '100%',
                    maxWidth: '640px',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    borderRadius: '8px',
                    fontFamily: '"Maersk Text", -apple-system, BlinkMacSystemFont, sans-serif',
                }}
            >
                {/* Header */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '20px 24px',
                        borderBottom: '1px solid #e0e0e0',
                        position: 'sticky',
                        top: 0,
                        backgroundColor: 'white',
                        zIndex: 10,
                    }}
                >
                    <div>
                        <h2
                            style={{
                                fontSize: '18px',
                                fontFamily: '"Maersk Text", sans-serif',
                                fontWeight: 500,
                                color: '#141414',
                                margin: 0,
                            }}
                        >
                            Share Subscription List
                        </h2>
                        <p
                            style={{
                                fontSize: '14px',
                                fontFamily: '"Maersk Text", sans-serif',
                                color: '#666',
                                marginTop: '4px',
                                margin: 0,
                            }}
                        >
                            {listName}
                        </p>
                    </div>
                    <button
                        onClick={handleClose}
                        style={{
                            padding: '6px',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#666',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '4px',
                            transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                        <CloseIcon style={{ fontSize: 20, color: '#666' }} />
                    </button>
                </div>

                {/* Content */}
                <div style={{ padding: '24px' }}>
                    {/* Email Input Section */}
                    <div style={{ marginBottom: '20px' }}>
                        <label
                            style={{
                                display: 'block',
                                fontSize: '14px',
                                fontFamily: '"Maersk Text", sans-serif',
                                fontWeight: 500,
                                color: '#141414',
                                marginBottom: '8px',
                            }}
                        >
                            Share with:
                        </label>

                        {/* Selected Emails Tags */}
                        {selectedEmails.length > 0 && (
                            <div
                                style={{
                                    marginBottom: '8px',
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '8px',
                                }}
                            >
                                {selectedEmails.map((item) => (
                                    <div
                                        key={item.email}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            padding: '6px 12px',
                                            backgroundColor: '#e3f2fd',
                                            border: '1px solid #42b0d5',
                                            borderRadius: '4px',
                                        }}
                                    >
                                    <span
                                        style={{
                                            fontSize: '14px',
                                            fontFamily: '"Maersk Text", sans-serif',
                                            color: '#141414',
                                        }}
                                    >
                                        {item.name || item.email}
                                    </span>

                                    {/* Permission Dropdown */}
                                    <div style={{ position: 'relative' }}>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setEditingPermissionFor(
                                                    editingPermissionFor === item.email
                                                        ? null
                                                        : item.email
                                                );
                                            }}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                padding: '4px 8px',
                                                border: '1px solid #42b0d5',
                                                borderRadius: '4px',
                                                backgroundColor: 'white',
                                                fontSize: '12px',
                                                fontFamily: '"Maersk Text", sans-serif',
                                                color: '#141414',
                                                cursor: 'pointer',
                                                transition: 'background-color 0.2s',
                                            }}
                                            onMouseEnter={(e) =>
                                                (e.currentTarget.style.backgroundColor = '#e3f2fd')
                                            }
                                            onMouseLeave={(e) =>
                                                (e.currentTarget.style.backgroundColor = 'white')
                                            }
                                        >
                                            {item.permission === 'read' ? 'Read' : 'Edit'}
                                            <KeyboardArrowDownIcon style={{ fontSize: 14 }} />
                                        </button>

                                        {/* Dropdown Menu */}
                                        {editingPermissionFor === item.email && (
                                            <div
                                                ref={dropdownRef}
                                                style={{
                                                    position: 'absolute',
                                                    top: '100%',
                                                    left: 0,
                                                    marginTop: '4px',
                                                    backgroundColor: 'white',
                                                    border: '1px solid #e0e0e0',
                                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                                    zIndex: 20,
                                                    minWidth: '140px',
                                                    borderRadius: '4px',
                                                }}
                                            >
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleUpdatePermission(item.email, 'read');
                                                    }}
                                                    style={{
                                                        width: '100%',
                                                        padding: '10px 12px',
                                                        border: 'none',
                                                        backgroundColor: 'white',
                                                        textAlign: 'left',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        fontSize: '14px',
                                                        fontFamily: '"Maersk Text", sans-serif',
                                                        color: '#141414',
                                                        transition: 'background-color 0.2s',
                                                    }}
                                                    onMouseEnter={(e) =>
                                                        (e.currentTarget.style.backgroundColor = '#f5f5f5')
                                                    }
                                                    onMouseLeave={(e) =>
                                                        (e.currentTarget.style.backgroundColor = 'white')
                                                    }
                                                >
                                                    Read Only
                                                    {item.permission === 'read' && (
                                                        <CheckIcon
                                                            style={{ fontSize: 16, color: '#42b0d5' }}
                                                        />
                                                    )}
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleUpdatePermission(item.email, 'write');
                                                    }}
                                                    style={{
                                                        width: '100%',
                                                        padding: '10px 12px',
                                                        border: 'none',
                                                        backgroundColor: 'white',
                                                        textAlign: 'left',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        fontSize: '14px',
                                                        fontFamily: '"Maersk Text", sans-serif',
                                                        color: '#141414',
                                                        transition: 'background-color 0.2s',
                                                    }}
                                                    onMouseEnter={(e) =>
                                                        (e.currentTarget.style.backgroundColor = '#f5f5f5')
                                                    }
                                                    onMouseLeave={(e) =>
                                                        (e.currentTarget.style.backgroundColor = 'white')
                                                    }
                                                >
                                                    Can Edit
                                                    {item.permission === 'write' && (
                                                        <CheckIcon
                                                            style={{ fontSize: 16, color: '#42b0d5' }}
                                                        />
                                                    )}
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveEmail(item.email);
                                        }}
                                        style={{
                                            padding: '2px',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '50%',
                                            transition: 'background-color 0.2s',
                                        }}
                                        onMouseEnter={(e) =>
                                            (e.currentTarget.style.backgroundColor = 'rgba(66, 176, 213, 0.2)')
                                        }
                                        onMouseLeave={(e) =>
                                            (e.currentTarget.style.backgroundColor = 'transparent')
                                        }
                                    >
                                        <CloseIcon style={{ fontSize: 14, color: '#141414' }} />
                                    </button>
                                </div>
                            ))}
                            </div>
                        )}

                        {/* McTypeahead Input */}
                        <McTypeahead
                            ref={typeaheadRef}
                            showlistonfocus={false}
                            maxoptions="20"
                            data={suggestions}
                            debounce="400"
                            label=""
                            placeholder="Search for users by name..."
                            hiddenlabel={true}
                            clearbutton={true}
                            name="usersearch"
                            search={async (e) => {
                                if (e.target.value.length < 3) {
                                    return;
                                }
                                if (typeaheadRef.current) {
                                    typeaheadRef.current.innerHTML = "";
                                    typeaheadRef.current.loading = true;
                                }
                                try {
                                    await fetchUserSuggestions(e.target.value);
                                } catch (error) {
                                    console.error('Error in fetchUserSuggestions:', error);
                                } finally {
                                    if (typeaheadRef.current) {
                                        typeaheadRef.current.loading = false;
                                    }
                                }
                            }}
                            optionselected={(e) => {
                                if (e.detail) {
                                    handleAddEmail(e.detail.sublabel, e.detail.label);
                                }
                            }}
                            clearbuttonclick={() => {
                                setSuggestions([]);
                                if (typeaheadRef.current) {
                                    typeaheadRef.current.data = [];
                                }
                            }}
                            optionsheight="200px"
                            minchars="3"
                            matchlabelonly
                            hint="Type a min of 3 characters to search"
                        />

                        <p
                            style={{
                                fontSize: '12px',
                                fontFamily: '"Maersk Text", sans-serif',
                                color: '#666',
                                marginTop: '8px',
                                margin: '8px 0 0 0',
                            }}
                        >
                            Type an email address or select from suggestions. Press Enter to add.
                            Click permission badge to change.
                        </p>
                    </div>

                    {/* Selected Count with Permission Breakdown */}
                    {selectedEmails.length > 0 && (
                        <div
                            style={{
                                padding: '12px 16px',
                                backgroundColor: '#f5f5f5',
                                border: '1px solid #e0e0e0',
                                borderRadius: '4px',
                                marginBottom: '20px',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <CheckIcon style={{ fontSize: 16, color: '#42b0d5' }} />
                                    <span
                                        style={{
                                            fontSize: '14px',
                                            fontFamily: '"Maersk Text", sans-serif',
                                            fontWeight: 500,
                                            color: '#141414',
                                        }}
                                    >
                                        {selectedEmails.length}{' '}
                                        {selectedEmails.length === 1 ? 'person' : 'people'} selected
                                    </span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    {readCount > 0 && (
                                        <span
                                            style={{
                                                fontSize: '13px',
                                                fontFamily: '"Maersk Text", sans-serif',
                                                color: '#666',
                                            }}
                                        >
                                            {readCount} Read
                                        </span>
                                    )}
                                    {editCount > 0 && (
                                        <span
                                            style={{
                                                fontSize: '13px',
                                                fontFamily: '"Maersk Text", sans-serif',
                                                color: '#666',
                                            }}
                                        >
                                            {editCount} Edit
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Message (Optional) */}
                    <div style={{ marginBottom: '20px' }}>
                        <label
                            style={{
                                display: 'block',
                                fontSize: '14px',
                                fontFamily: '"Maersk Text", sans-serif',
                                fontWeight: 500,
                                color: '#141414',
                                marginBottom: '8px',
                            }}
                        >
                            Message{' '}
                            <span
                                style={{
                                    color: '#999',
                                    fontWeight: 'normal',
                                }}
                            >
                                (Optional)
                            </span>
                        </label>
                        <textarea
                            rows={3}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Add a message to include with the share..."
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '4px',
                                fontSize: '14px',
                                fontFamily: '"Maersk Text", sans-serif',
                                color: '#141414',
                                outline: 'none',
                                resize: 'vertical',
                                transition: 'border-color 0.2s',
                            }}
                            onFocus={(e) => (e.currentTarget.style.borderColor = '#42b0d5')}
                            onBlur={(e) => (e.currentTarget.style.borderColor = '#e0e0e0')}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: '12px',
                        padding: '16px 24px',
                        borderTop: '1px solid #e0e0e0',
                        backgroundColor: '#fafafa',
                        position: 'sticky',
                        bottom: 0,
                    }}
                >
                    <button
                        onClick={handleClose}
                        disabled={isSharing}
                        style={{
                            padding: '8px 20px',
                            backgroundColor: '#f5f5f5',
                            color: '#333',
                            border: 'none',
                            borderRadius: '5px',
                            fontSize: '14px',
                            fontWeight: 500,
                            fontFamily: '"Maersk Text", sans-serif',
                            cursor: isSharing ? 'not-allowed' : 'pointer',
                            transition: 'background-color 0.2s',
                            opacity: isSharing ? 0.6 : 1,
                        }}
                        onMouseEnter={(e) => {
                            if (!isSharing) e.currentTarget.style.backgroundColor = '#e0e0e0';
                        }}
                        onMouseLeave={(e) => {
                            if (!isSharing) e.currentTarget.style.backgroundColor = '#f5f5f5';
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleShare}
                        disabled={isSharing || selectedEmails.length === 0}
                        style={{
                            padding: '8px 20px',
                            backgroundColor:
                                isSharing || selectedEmails.length === 0 ? '#ccc' : '#141414',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            fontSize: '14px',
                            fontWeight: 500,
                            fontFamily: '"Maersk Text", sans-serif',
                            cursor:
                                isSharing || selectedEmails.length === 0
                                    ? 'not-allowed'
                                    : 'pointer',
                            transition: 'background-color 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                        onMouseEnter={(e) => {
                            if (!isSharing && selectedEmails.length > 0) {
                                e.currentTarget.style.backgroundColor = '#363636';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isSharing && selectedEmails.length > 0) {
                                e.currentTarget.style.backgroundColor = '#141414';
                            }
                        }}
                    >
                        {isSharing ? (
                            <>
                                <CircularProgress size={16} style={{ color: 'white' }} />
                                Sharing...
                            </>
                        ) : (
                            `Share List`
                        )}
                    </button>
                </div>
            </div>
        </>
    );
};

export default ShareModal;
